import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Database, Brain, Fuel, Volume2, ChevronDown, Shield,
  BarChart3, Globe, Loader2, Play, CheckCircle,
} from 'lucide-react';
import { API as API_BASE } from '../lib/api';

// ── Usage Gas Gauge ──────────────────────────────────────────────────
const GasGauge = ({ token, restaurantId = 'default' }) => {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/moat/usage/${restaurantId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(setUsage)
      .catch(() => {});
  }, [token, restaurantId]);

  if (!usage) return null;

  const pct = Math.min(usage.usage_pct, 100);
  const color = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#10b981';
  const radius = 44;
  const circ = 2 * Math.PI * radius;
  const dash = (pct / 100) * circ;

  return (
    <div data-testid="gas-gauge" className="p-5 rounded-xl bg-white/5 border border-white/10">
      <div className="flex items-center gap-2 mb-4">
        <Fuel className="w-4 h-4 text-[#D4AF37]" />
        <span className="text-white font-medium text-sm">Usage Gauge</span>
        <span className="ml-auto text-xs text-gray-500">{usage.month}</span>
      </div>
      <div className="flex items-center gap-6">
        {/* Circular gauge */}
        <div className="relative flex-shrink-0" style={{ width: 100, height: 100 }}>
          <svg width="100" height="100" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="50" cy="50" r={radius} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="8" />
            <circle cx="50" cy="50" r={radius} fill="none" stroke={color} strokeWidth="8"
              strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 0.8s ease' }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-bold" style={{ color }}>{pct.toFixed(0)}%</span>
            <span className="text-[9px] text-gray-600">used</span>
          </div>
        </div>
        {/* Stats */}
        <div className="flex-1 space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-500">Minutes Used</span>
            <span className="text-white font-medium">{usage.total_minutes.toFixed(0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Founding Cap</span>
            <span className="text-white font-medium">{usage.cap.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Calls This Month</span>
            <span className="text-white font-medium">{usage.call_count}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Alert At</span>
            <span className="text-amber-400 font-medium">{usage.alert_threshold_pct}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Voice Profile Selector ──────────────────────────────────────────
const VoiceSelector = ({ token, restaurantId = 'default' }) => {
  const [profiles, setProfiles] = useState({});
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/moat/voice-profiles`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setProfiles(d.profiles || {}))
      .catch(() => {});

    // Get current selection
    fetch(`${API_BASE}/config/${restaurantId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setSelected(d.voice_profile || 'executive'))
      .catch(() => setSelected('executive'));
  }, [token, restaurantId]);

  const selectProfile = async (key) => {
    setSaving(true);
    try {
      await fetch(`${API_BASE}/moat/voice-select`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurant_id: restaurantId, voice_profile: key }),
      });
      setSelected(key);
    } catch {}
    setSaving(false);
  };

  const iconColors = { executive: '#D4AF37', concierge: '#8b5cf6', specialist: '#10b981' };

  return (
    <div data-testid="voice-selector" className="p-5 rounded-xl bg-white/5 border border-white/10">
      <div className="flex items-center gap-2 mb-4">
        <Volume2 className="w-4 h-4 text-[#D4AF37]" />
        <span className="text-white font-medium text-sm">Voice Profile</span>
      </div>
      <div className="space-y-2">
        {Object.entries(profiles).map(([key, profile]) => (
          <button
            key={key}
            data-testid={`voice-profile-${key}`}
            onClick={() => selectProfile(key)}
            disabled={saving}
            className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all"
            style={{
              background: selected === key ? `${iconColors[key]}12` : 'rgba(255,255,255,0.03)',
              border: `1px solid ${selected === key ? `${iconColors[key]}40` : 'rgba(255,255,255,0.08)'}`,
            }}
          >
            <div className="p-2 rounded-lg" style={{ background: `${iconColors[key]}18` }}>
              <Volume2 className="w-4 h-4" style={{ color: iconColors[key] }} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-white text-sm font-medium">{profile.name}</span>
              <p className="text-gray-500 text-xs truncate">{profile.description}</p>
            </div>
            {selected === key && <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: iconColors[key] }} />}
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Data Consent Toggle ─────────────────────────────────────────────
const DataConsentToggle = ({ token, restaurantId = 'default' }) => {
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/config/${restaurantId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setConsent(d.global_brain_consent || false))
      .catch(() => {});
  }, [token, restaurantId]);

  const toggle = async () => {
    const newVal = !consent;
    try {
      await fetch(`${API_BASE}/moat/data-consent`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurant_id: restaurantId, consent: newVal }),
      });
      setConsent(newVal);
    } catch {}
  };

  return (
    <div className="p-4 rounded-xl bg-white/5 border border-white/10 flex items-center gap-4">
      <div className="p-2 rounded-lg bg-blue-500/10">
        <Shield className="w-4 h-4 text-blue-400" />
      </div>
      <div className="flex-1">
        <p className="text-white text-sm font-medium">Global Brain Contribution</p>
        <p className="text-gray-600 text-xs">Share anonymized intent data to improve AI for everyone</p>
      </div>
      <button
        data-testid="data-consent-toggle"
        onClick={toggle}
        className="relative w-12 h-6 rounded-full transition-colors"
        style={{ background: consent ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.1)' }}
      >
        <div
          className="absolute top-0.5 w-5 h-5 rounded-full transition-all"
          style={{
            left: consent ? '26px' : '2px',
            background: consent ? '#10b981' : '#6b7280',
          }}
        />
      </button>
    </div>
  );
};

// ── Training Data Stats ─────────────────────────────────────────────
const TrainingStats = ({ token }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/moat/training-data/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(setStats)
      .catch(() => {});
  }, [token]);

  if (!stats) return null;

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
        <Database className="w-5 h-5 text-[#D4AF37] mx-auto mb-2" />
        <p className="text-2xl font-bold text-white">{stats.training_records.toLocaleString()}</p>
        <p className="text-xs text-gray-500">Training Records</p>
      </div>
      <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
        <Brain className="w-5 h-5 text-purple-400 mx-auto mb-2" />
        <p className="text-2xl font-bold text-white">{stats.global_brain_records.toLocaleString()}</p>
        <p className="text-xs text-gray-500">Global Brain</p>
      </div>
      <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
        <Globe className="w-5 h-5 text-blue-400 mx-auto mb-2" />
        <p className="text-2xl font-bold text-white">{stats.unique_intents}</p>
        <p className="text-xs text-gray-500">Unique Intents</p>
      </div>
    </div>
  );
};

// ── Synthetic Generator Button ──────────────────────────────────────
const SyntheticGenerator = ({ token }) => {
  const [running, setRunning] = useState(false);
  const [runningEdge, setRunningEdge] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (type = 'standard') => {
    const isEdge = type === 'edge';
    isEdge ? setRunningEdge(true) : setRunning(true);
    setResult(null);
    try {
      const endpoint = isEdge ? '/moat/synthetic/edge-cases' : '/moat/synthetic/generate';
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      setResult(data);
    } catch (e) {
      setResult({ error: e.message });
    }
    isEdge ? setRunningEdge(false) : setRunning(false);
  };

  return (
    <div className="p-5 rounded-xl bg-gradient-to-r from-purple-500/8 to-blue-500/5 border border-purple-500/20">
      <div className="flex items-center gap-2 mb-3">
        <Brain className="w-4 h-4 text-purple-400" />
        <span className="text-white font-medium text-sm">Synthetic Moat Generator</span>
      </div>
      <p className="text-gray-500 text-xs mb-4">
        AI-to-AI simulations: Pizza Shop, Italian, Food Truck, Greek Taverna, Coffee Shop
      </p>
      <div className="flex gap-3">
        <button
          data-testid="run-synthetic-generator"
          onClick={() => run('standard')}
          disabled={running}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all"
          style={{ background: 'rgba(139,92,246,0.2)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)' }}
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? 'Generating...' : 'Standard (500)'}
        </button>
        <button
          data-testid="run-edge-case-generator"
          onClick={() => run('edge')}
          disabled={runningEdge}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all"
          style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}
        >
          {runningEdge ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {runningEdge ? 'Generating...' : 'Edge Cases (500)'}
        </button>
      </div>
      {result && !result.error && (
        <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <p className="text-emerald-400 text-xs font-medium">{result.message}</p>
        </div>
      )}
    </div>
  );
};

// ── Main Moat Tab ───────────────────────────────────────────────────
const MoatTab = ({ token }) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-purple-600/8 to-[#D4AF37]/5 border border-purple-500/20">
        <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 shadow-lg">
          <Database className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Data Moat & Infrastructure</h2>
          <p className="text-gray-400 text-sm">Usage tracking, voice profiles, training data, and the Global Argus Brain</p>
        </div>
      </div>

      {/* Top row: Gas Gauge + Voice Selector */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <GasGauge token={token} />
        <VoiceSelector token={token} />
      </div>

      {/* Data Consent */}
      <DataConsentToggle token={token} />

      {/* Training Stats */}
      <TrainingStats token={token} />

      {/* Synthetic Generator */}
      <SyntheticGenerator token={token} />
    </div>
  );
};

export default MoatTab;
